import React from 'react';

const badgeByOutcome = {
  interested: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  not_interested: 'bg-rose-100 text-rose-800 border-rose-300',
  callback: 'bg-amber-100 text-amber-800 border-amber-300',
  no_answer: 'bg-slate-200 text-slate-800 border-slate-300',
  call_later: 'bg-blue-100 text-blue-800 border-blue-300'
};

const formatLabel = (value) => {
  if (!value) return '';
  return String(value)
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const hasMeaningfulBusinessName = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return Boolean(normalized && normalized !== 'not specified' && normalized !== '--' && normalized !== 'n/a');
};

const CallDetailsModal = ({
  open,
  call,
  followUps,
  loading = false,
  onClose,
  onAddFollowUp,
  onEditFollowUp,
  onDeleteFollowUp,
  formatDateTime,
  outcomeLabel = {}
}) => {
  if (!open || !call) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-3">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] shadow-xl border border-slate-200 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Call Details</h3>
            <p className="text-[11px] text-slate-500">
              <span className="font-medium">{call?.contactId?.name || '--'}</span>
              {call?.contactId?.phone ? ` | ${call.contactId.phone}` : ''}
            </p>
          </div>
          <button type="button" onClick={onClose} className="h-8 w-8 rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50">
            <i className="bi bi-x"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-3 py-2.5 space-y-2.5">
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-3 py-2 border-b border-slate-100 bg-[linear-gradient(135deg,#f8fafc_0%,#eef6ff_100%)]">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Call Summary</p>
                  </div>
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${call?.callType === 'incoming' ? 'bg-sky-100 text-sky-700' : 'bg-violet-100 text-violet-700'}`}>
                    {call?.callType ? formatLabel(call.callType) : '--'}
                  </span>
                </div>
              </div>
              <div className="px-2.5 py-2 overflow-x-auto">
                <div className="flex items-stretch gap-1.5 min-w-max">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 min-w-[108px]">
                    <p className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold mb-0.5">Call Status</p>
                    <p className="text-[11px] font-semibold text-slate-900 leading-4 truncate">{formatLabel(call?.lastFollowUp?.callStatus) || '--'}</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 min-w-[108px]">
                    <p className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold mb-0.5">Created</p>
                    <p className="text-[11px] font-semibold text-slate-900 leading-4 truncate">{call?.createdAt ? new Date(call.createdAt).toLocaleDateString('en-IN') : '--'}</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 min-w-[108px]">
                    <p className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold mb-0.5">Updated</p>
                    <p className="text-[11px] font-semibold text-slate-900 leading-4 truncate">{call?.updatedAt ? new Date(call.updatedAt).toLocaleDateString('en-IN') : '--'}</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 min-w-[176px]">
                    <p className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold mb-0.5">{hasMeaningfulBusinessName(call?.contactId?.businessName) ? 'Business' : 'Name'}</p>
                    <p className="text-[11px] text-slate-800 font-medium leading-4 truncate">
                      {hasMeaningfulBusinessName(call?.contactId?.businessName)
                        ? `${call.contactId.businessName} (${call?.contactId?.name || '--'})`
                        : (call?.contactId?.name || '--')}
                    </p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 min-w-[112px]">
                    <p className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold mb-0.5">Phone</p>
                    <p className="text-[11px] text-slate-800 font-medium leading-4 truncate">{call?.contactId?.phone || '--'}</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 min-w-[160px]">
                    <p className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold mb-0.5">Email</p>
                    <p className="text-[11px] text-slate-800 font-medium leading-4 truncate">{call?.contactId?.email || '--'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 overflow-hidden min-h-[340px] flex flex-col">
              <div className="px-3 py-2 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200 flex items-center justify-between">
                <h4 className="text-xs font-semibold text-slate-900 flex items-center gap-1.5">
                  <i className="bi bi-clock-history text-emerald-600"></i>
                  Follow-up History
                </h4>
                <button type="button" onClick={onAddFollowUp} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-100 text-emerald-800 text-[11px] font-semibold hover:bg-emerald-200 transition">
                  <i className="bi bi-plus-circle"></i>
                  Add Follow-up
                </button>
              </div>

              {loading ? (
                <div className="p-4 text-center flex-1 flex flex-col items-center justify-center">
                  <div className="inline-block h-6 w-6 rounded-full border-2 border-slate-300 border-t-blue-600 animate-spin"></div>
                  <p className="text-xs text-slate-500 mt-1">Loading...</p>
                </div>
              ) : (followUps?.length || 0) === 0 ? (
                <div className="p-4 text-center text-slate-500 flex-1 flex flex-col items-center justify-center">
                  <p className="text-xs">No follow-ups</p>
                </div>
              ) : (
                <div className="overflow-x-auto flex-1 max-h-[420px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-left text-[10px] uppercase text-slate-600 font-semibold">
                      <tr>
                        <th className="px-3 py-2 border-b border-slate-200">Created Date</th>
                        <th className="px-3 py-2 border-b border-slate-200">Call Status</th>
                        <th className="px-3 py-2 border-b border-slate-200">Outcome</th>
                        <th className="px-3 py-2 border-b border-slate-200">Next Follow-up</th>
                        <th className="px-3 py-2 border-b border-slate-200">Note</th>
                        {(onEditFollowUp || onDeleteFollowUp) && (
                          <th className="px-3 py-2 border-b border-slate-200 text-center">Actions</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {followUps.map((item) => (
                        <tr key={item?._id} className="hover:bg-slate-50 transition">
                          <td className="px-3 py-2 text-slate-700 text-xs">{formatDateTime?.(item?.createdAt) || '--'}</td>
                          <td className="px-3 py-2 text-center">
                            {item?.callStatus ? (
                              <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700">
                                {formatLabel(item.callStatus)}
                              </span>
                            ) : '--'}
                          </td>
                          <td className="px-3 py-2">
                            {item?.outcome ? (
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${badgeByOutcome[item.outcome] || 'bg-slate-100 text-slate-700'}`}>
                                {outcomeLabel[item.outcome] || formatLabel(item.outcome)}
                              </span>
                            ) : '--'}
                          </td>
                          <td className="px-3 py-2 text-slate-700 text-xs">{formatDateTime?.(item?.nextFollowDateTime || item?.followUpDateTime) || '--'}</td>
                          <td className="px-3 py-2 text-slate-700 max-w-xs truncate text-xs" title={item?.note || ''}>{item?.note || '--'}</td>
                          {(onEditFollowUp || onDeleteFollowUp) && (
                            <td className="px-3 py-2">
                              <div className="flex items-center justify-center gap-1">
                                {onEditFollowUp && (
                                  <button type="button" onClick={() => onEditFollowUp(item)} className="inline-flex items-center px-2 py-1 rounded-md bg-blue-100 text-blue-800 text-[10px] font-semibold hover:bg-blue-200">
                                    Edit
                                  </button>
                                )}
                                {onDeleteFollowUp && (
                                  <button type="button" onClick={() => onDeleteFollowUp(item)} className="inline-flex items-center px-2 py-1 rounded-md bg-rose-100 text-rose-800 text-[10px] font-semibold hover:bg-rose-200">
                                    Delete
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallDetailsModal;
